import { useCallback } from 'react';
import { HeaderConnectionBannerItemId } from '@headless-workspace/core-domain';
import { ConnectionDataLayerLabel, DataLayerEvent, DataLayerEventCategory, useDataLayer } from '../../../../client';

interface UseHeaderEventHandlersProps {
    trackEvent: ReturnType<typeof useDataLayer>['trackEvent'];
    trackT2SEvent?: ReturnType<typeof useDataLayer>['trackT2SEvent'];
    dataLayerConnectionAction: string;
}

export const useHeaderEventHandlers = ({
    trackEvent,
    trackT2SEvent,
    dataLayerConnectionAction,
}: UseHeaderEventHandlersProps) => {
    const connectionEventClick = useCallback(() => {
        trackEvent(DataLayerEvent.Click, {
            id: 'click',
            category: DataLayerEventCategory.Banners,
            action: dataLayerConnectionAction,
        });
    }, [dataLayerConnectionAction, trackEvent]);

    const t2SProductEventClick = useCallback(
        (trackingId: string) => () => {
            if (trackT2SEvent) {
                trackT2SEvent({ trackingId });
            }
        },
        [trackT2SEvent],
    );

    const connectionBannerEventClick = useCallback(
        (dataLayerLabel: string) => () => {
            let eventCategory = DataLayerEventCategory.Banners;
            let eventAction = dataLayerConnectionAction;

            if (dataLayerLabel === ConnectionDataLayerLabel[HeaderConnectionBannerItemId.MyOrders]) {
                eventCategory = DataLayerEventCategory.ClientAccount;
                eventAction = 'order_history';
            } else if (dataLayerLabel === ConnectionDataLayerLabel[HeaderConnectionBannerItemId.FollowMyOrder]) {
                eventCategory = DataLayerEventCategory.ClientAccount;
                eventAction = 'ongoing_order';
            }

            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                label: dataLayerLabel,
                category: eventCategory,
                action: eventAction,
            });
        },
        [dataLayerConnectionAction, trackEvent],
    );

    const categoryEventClick = useCallback(
        (dataLayerId: string) => () => {
            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                category: DataLayerEventCategory.Navigation,
                action: `click-${dataLayerId}`,
            });
        },
        [trackEvent],
    );

    const nestedCategoryEventClick = useCallback(
        (dataLayerId: string, dataLayerSubId: string) => () => {
            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                category: DataLayerEventCategory.Navigation,
                action: `click-${dataLayerId}`,
                label: dataLayerId,
                event_extra_dim1: dataLayerSubId,
            });
        },
        [trackEvent],
    );

    const persistentBannerEventClick = useCallback(
        (dataLayerId: string) => () =>
            trackEvent(DataLayerEvent.Click, {
                id: 'click',
                category: DataLayerEventCategory.Animation,
                event: 'Animation',
                action: 'persistent banner',
                label: dataLayerId,
            }),
        [trackEvent],
    );

    return {
        connectionEventClick,
        t2SProductEventClick,
        connectionBannerEventClick,
        categoryEventClick,
        nestedCategoryEventClick,
        persistentBannerEventClick,
    };
};
